import { useCallback, useEffect, useMemo, useState } from "react";

import {
  api as accountsApi,
  tradeLogApi,
  type Account,
  type ManualTrade,
  ApiError,
} from "../lib/api";
import PnlChart, { type ChartSeries, type ChartPoint } from "../components/PnlChart";

type ChartMode = "combined" | "byAccount";
type RangeKey = "all" | "1y" | "90d" | "30d";

const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: "all", label: "All", days: null },
  { key: "1y", label: "1Y", days: 365 },
  { key: "90d", label: "90D", days: 90 },
  { key: "30d", label: "30D", days: 30 },
];

// Distinct hues that read acceptably on both light and dark backgrounds.
const PALETTE = [
  "#0a84ff", "#34c759", "#ff9500", "#ff375f", "#af52de", "#5ac8fa",
  "#ffcc00", "#ff2d55", "#30d158", "#bf5af2", "#64d2ff", "#ac8e68",
];
const POS = "#34c759";
const NEG = "#ff375f";

/**
 * /dashboard — an at-a-glance overview of the current state of affairs.
 *
 * Everything is computed client-side from the existing list endpoints
 * (no dedicated stats API). Metrics:
 *   1. Number of active accounts
 *   2. Cumulative funded capital (active accounts only)
 *   3. Cumulative P/L across all manual trades
 *   4. % breakdown of trades by strategy
 *   5. % breakdown of trades by instrument
 *
 * Trade-derived metrics use the manual trade log (ccc-manual-trades).
 */
export default function Dashboard() {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [trades, setTrades] = useState<ManualTrade[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chartMode, setChartMode] = useState<ChartMode>("combined");
  const [range, setRange] = useState<RangeKey>("all");

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [a, t] = await Promise.all([accountsApi.list(), tradeLogApi.list()]);
      setAccounts(a);
      setTrades(t);
    } catch (e) {
      setError(messageFrom(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const metrics = useMemo(() => {
    if (!accounts || !trades) return null;

    const active = accounts.filter((a) => a.active);
    const activeCount = active.length;
    const fundedCapital = active.reduce((sum, a) => sum + (a.size || 0), 0);
    const cumulativePnl = trades.reduce((sum, t) => sum + (t.pnl || 0), 0);

    const byStrategy = breakdown(
      trades.map((t) => (t.strategy && t.strategy.trim()) || "Unassigned"),
    );
    const byInstrument = breakdown(trades.map((t) => t.instrument || "—"));

    return {
      activeCount,
      fundedCapital,
      cumulativePnl,
      tradeCount: trades.length,
      byStrategy,
      byInstrument,
    };
  }, [accounts, trades]);

  const accountName = useMemo(() => {
    const m = new Map<string, string>();
    (accounts ?? []).forEach((a) => m.set(a.account_id, a.name));
    return m;
  }, [accounts]);

  const chart = useMemo(() => {
    if (!trades) return null;

    const parsed = trades
      .map((t) => ({
        t: Date.parse(t.entry_date),
        pnl: t.pnl || 0,
        account_id: t.account_id,
      }))
      .filter((p) => Number.isFinite(p.t));

    if (parsed.length === 0) {
      return { series: [] as ChartSeries[], total: 0, hasData: false };
    }

    const globalMin = Math.min(...parsed.map((p) => p.t));
    const globalMax = Math.max(...parsed.map((p) => p.t));
    const days = RANGES.find((r) => r.key === range)?.days ?? null;
    const cutoff = days == null ? -Infinity : Date.now() - days * 86400000;
    const visibleStart = Math.max(cutoff, globalMin);
    const visibleEnd = globalMax;

    let series: ChartSeries[];
    if (chartMode === "combined") {
      const points = buildSeriesPoints(parsed, visibleStart, visibleEnd);
      const finalV = points.length ? points[points.length - 1].v : 0;
      series = [
        {
          key: "all",
          label: "All accounts",
          color: finalV >= 0 ? POS : NEG,
          points,
        },
      ];
    } else {
      const groups = new Map<string, { t: number; pnl: number }[]>();
      for (const p of parsed) {
        const g = groups.get(p.account_id) ?? [];
        g.push({ t: p.t, pnl: p.pnl });
        groups.set(p.account_id, g);
      }
      series = [...groups.entries()]
        .sort((a, b) =>
          (accountName.get(a[0]) ?? a[0]).localeCompare(accountName.get(b[0]) ?? b[0]),
        )
        .map(([account_id, rows], i) => ({
          key: account_id,
          label: accountName.get(account_id) ?? "(deleted account)",
          color: PALETTE[i % PALETTE.length],
          points: buildSeriesPoints(rows, visibleStart, visibleEnd),
        }));
    }

    // P/L realized within the visible window = end value minus the carried-in
    // baseline, summed across series.
    const total = series.reduce((sum, s) => {
      const first = s.points[0];
      const last = s.points[s.points.length - 1];
      return sum + ((last?.v ?? 0) - (first?.v ?? 0));
    }, 0);

    return { series, total, hasData: true };
  }, [trades, chartMode, range, accountName]);

  return (
    <section className="accounts">
      <header className="accounts-header">
        <div>
          <h1>Dashboard</h1>
          <div className="muted">Current state of affairs across Cosmic Cow Capital.</div>
        </div>
        <div className="header-actions">
          <button className="link-button" onClick={() => void refresh()}>
            Refresh
          </button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {metrics === null ? (
        <p>Loading&hellip;</p>
      ) : (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Active accounts</div>
              <div className="stat-value">{metrics.activeCount.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Funded capital</div>
              <div className="stat-value">{formatUsd(metrics.fundedCapital)}</div>
            </div>
            <div
              className={
                "stat-card " +
                (metrics.cumulativePnl > 0
                  ? "stat-pos"
                  : metrics.cumulativePnl < 0
                    ? "stat-neg"
                    : "")
              }
            >
              <div className="stat-label">Cumulative P/L</div>
              <div className="stat-value">{formatPnl(metrics.cumulativePnl)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Logged trades</div>
              <div className="stat-value">{metrics.tradeCount.toLocaleString()}</div>
            </div>
          </div>

          {chart && (
            <div className="chart-card">
              <div className="chart-controls">
                <div className="chart-title-row">
                  <h2 className="chart-heading">Cumulative P/L</h2>
                  <span
                    className={
                      "chart-total " + (chart.total >= 0 ? "pnl-pos" : "pnl-neg")
                    }
                  >
                    {formatPnl(chart.total)}
                  </span>
                  <span className="muted">{rangeLabel(range)}</span>
                </div>
                <div className="chart-control-group">
                  <div className="segmented" role="group" aria-label="Chart mode">
                    <button
                      className={chartMode === "combined" ? "active" : ""}
                      onClick={() => setChartMode("combined")}
                    >
                      Combined
                    </button>
                    <button
                      className={chartMode === "byAccount" ? "active" : ""}
                      onClick={() => setChartMode("byAccount")}
                    >
                      By account
                    </button>
                  </div>
                  <div className="segmented" role="group" aria-label="Time range">
                    {RANGES.map((r) => (
                      <button
                        key={r.key}
                        className={range === r.key ? "active" : ""}
                        onClick={() => setRange(r.key)}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {chart.hasData ? (
                <>
                  <PnlChart series={chart.series} />
                  <div className="chart-legend">
                    {chart.series.map((s) => (
                      <span key={s.key} className="chart-legend-item">
                        <span className="chart-swatch" style={{ background: s.color }} />
                        {s.label}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div className="chart-empty muted">
                  No trades logged yet — add some on the Trade log page.
                </div>
              )}
            </div>
          )}

          <div className="breakdown-grid">
            <Breakdown
              title="Trades by strategy"
              rows={metrics.byStrategy}
              total={metrics.tradeCount}
            />
            <Breakdown
              title="Trades by instrument"
              rows={metrics.byInstrument}
              total={metrics.tradeCount}
            />
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Breakdown widget
// ---------------------------------------------------------------------------

interface BreakdownRow {
  key: string;
  count: number;
  pct: number;
}

function Breakdown({
  title,
  rows,
  total,
}: {
  title: string;
  rows: BreakdownRow[];
  total: number;
}) {
  return (
    <div className="breakdown-card">
      <h2 className="breakdown-title">{title}</h2>
      {total === 0 || rows.length === 0 ? (
        <p className="muted">No trades logged yet.</p>
      ) : (
        <ul className="breakdown">
          {rows.map((r) => (
            <li key={r.key} className="breakdown-row">
              <div className="breakdown-head">
                <span className="breakdown-label">{r.key}</span>
                <span className="breakdown-pct">
                  {r.pct.toFixed(1)}% <span className="muted">({r.count})</span>
                </span>
              </div>
              <div className="breakdown-bar">
                <div className="breakdown-fill" style={{ width: `${r.pct}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Count occurrences of each label and return rows sorted by count desc. */
function breakdown(labels: string[]): BreakdownRow[] {
  const total = labels.length;
  const counts = new Map<string, number>();
  for (const label of labels) {
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([key, count]) => ({
      key,
      count,
      pct: total === 0 ? 0 : (count / total) * 100,
    }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

/**
 * Build a cumulative-P/L curve for one set of trade rows, clipped to a visible
 * window. The curve carries the equity accrued *before* the window in as the
 * starting baseline at `visibleStart`, and is extended flat to `visibleEnd` so
 * every series spans the full chart width.
 */
function buildSeriesPoints(
  rows: { t: number; pnl: number }[],
  visibleStart: number,
  visibleEnd: number,
): ChartPoint[] {
  const sorted = [...rows].sort((a, b) => a.t - b.t);

  // Equity accrued strictly before the window enters.
  let base = 0;
  for (const r of sorted) {
    if (r.t < visibleStart) base += r.pnl;
    else break;
  }

  const out: ChartPoint[] = [{ t: visibleStart, v: base }];
  let sum = base;
  for (const r of sorted) {
    if (r.t < visibleStart) continue;
    sum += r.pnl;
    out.push({ t: r.t, v: sum });
  }

  const last = out[out.length - 1];
  if (last.t < visibleEnd) out.push({ t: visibleEnd, v: last.v });
  return out;
}

function rangeLabel(range: RangeKey): string {
  switch (range) {
    case "1y":
      return "Last 12 months";
    case "90d":
      return "Last 90 days";
    case "30d":
      return "Last 30 days";
    default:
      return "All time";
  }
}

function formatUsd(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatPnl(n: number): string {
  const sign = n > 0 ? "+" : "";
  return (
    sign +
    n.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function messageFrom(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}
